/**
 * Uygulamanın en dış hata sınırı.
 *
 * Bir ekran render sırasında çökerse kullanıcı beyaz sayfa yerine yenileme
 * düğmesi görür ve olay ekibe bildirilir (services/errorReporter). Deploy
 * sonrası eski sayfa dosyası yüklenemediğinde de buraya düşer; yenilemek yeni
 * dosyaları getirir. Bölüm bazlı sınırlar (AgentHealthErrorBoundary gibi)
 * kendi hatalarını önce yakalar; bu, yakalanmayan her şey için son ağ.
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import i18n from '../../i18n';
import { isChunkError, reportClientError } from '../../services/errorReporter';
import './AppErrorBoundary.scss';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

export class AppErrorBoundary extends Component<Props, State> {
  public state: State = { hasError: false };

  public static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  public componentDidCatch(error: Error, info: ErrorInfo): void {
    const component = (info.componentStack || '').trim().split('\n')[0]?.trim() ?? '';
    reportClientError({
      kind: isChunkError(error) ? 'chunk' : 'render',
      message: component ? `${error.message} (${component})` : error.message,
    });
  }

  private reload = (): void => {
    window.location.reload();
  };

  public render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="app-error-boundary" role="alert">
        <h1 className="app-error-boundary__title">{i18n.t('errors:appCrash.title')}</h1>
        <p className="app-error-boundary__body">{i18n.t('errors:appCrash.body')}</p>
        <button type="button" className="app-error-boundary__reload" onClick={this.reload}>
          {i18n.t('errors:appCrash.reload')}
        </button>
      </div>
    );
  }
}

export default AppErrorBoundary;
